/**
 * Timbrado e folha de impressão alinhados aos Recibos (logo, cores, tipografia).
 * Usado em impressos agrupados da evolução e documentos clínicos simples.
 */

export function escapeHtml(s: string | undefined | null): string {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Bloco interno do cabeçalho (logo + centro + meta) — mesmo padrão de Recibos.tsx */
export function buildClinicLetterheadBlockHtml(c: any): string {
  if (!c) {
    return `
      <div class="doc-head-logo doc-head-logo--empty"></div>
      <div class="doc-head-center">
        <div class="clinic-name">Clínica</div>
        <div class="clinic-subtitle">Configure nome, endereço e logo em Configurações da Clínica</div>
      </div>
      <div class="doc-head-meta"></div>`;
  }
  const logo = c.logo_url
    ? `<div class="doc-head-logo"><img src="${escapeHtml(c.logo_url)}" alt="" /></div>`
    : '<div class="doc-head-logo doc-head-logo--empty"></div>';
  const subtitle = c.clinic_subtitle
    ? `<div class="clinic-subtitle">${escapeHtml(c.clinic_subtitle)}</div>`
    : '';
  const cnpjLine = c.cnpj ? `<div>CNPJ: ${escapeHtml(c.cnpj)}</div>` : '';
  let addr = '';
  if (c.address_street) {
    addr = `<div>${escapeHtml(c.address_street)}${c.address_number ? `, ${escapeHtml(c.address_number)}` : ''}${c.address_neighborhood ? ` — ${escapeHtml(c.address_neighborhood)}` : ''}</div>`;
  }
  const cityLine =
    c.address_city || c.address_state || c.address_zip
      ? `<div>${[c.address_city, c.address_state].filter(Boolean).map(escapeHtml).join(' - ')}${c.address_zip ? ` • CEP ${escapeHtml(c.address_zip)}` : ''}</div>`
      : '';
  const tel =
    c.phone || c.whatsapp ? `<div>Tel: ${escapeHtml(c.phone || c.whatsapp)}</div>` : '';
  const mail = c.email ? `<div>${escapeHtml(c.email)}</div>` : '';
  const site = c.website ? `<div>${escapeHtml(c.website)}</div>` : '';

  return `
    ${logo}
    <div class="doc-head-center">
      <div class="clinic-name">${escapeHtml(c.clinic_name || 'Clínica')}</div>
      ${subtitle}
    </div>
    <div class="doc-head-meta">
      ${cnpjLine}
      ${addr}
      ${cityLine}
      ${tel}
      ${mail}
      ${site}
    </div>`;
}

export interface ClinicPrintDocumentOptions {
  clinic: any | null;
  browserTitle: string;
  /** Pill central (ex.: Documentos da evolução) */
  badgeText?: string;
  /** Linha discreta abaixo do badge (ex.: data) */
  subtitleBelowBadge?: string;
  bodyHtml: string;
  footerNote?: string;
}

/** Uma folha timbrada no pacote de impressão (sem badge global). */
export interface ClinicPrintSheetSpec {
  bodyHtml: string;
  footerNote?: string;
}

/** Vários documentos: um timbre por item, cada um começa em nova página na impressão. Sem pill/badge comum. */
export function buildClinicMultiSheetPrintHtml(opts: {
  clinic: any | null;
  browserTitle: string;
  sheets: ClinicPrintSheetSpec[];
}): string {
  if (!opts.sheets.length) {
    return buildClinicPrintDocumentHtml({
      clinic: opts.clinic,
      browserTitle: opts.browserTitle,
      bodyHtml: '',
    });
  }

  const sheetsHtml = opts.sheets
    .map((s, i) => {
      const foot = s.footerNote ? `<div class="footer-note">${escapeHtml(s.footerNote)}</div>` : '';
      const cls = i === 0 ? 'sheet' : 'sheet sheet--page-break';
      return `<div class="${cls}">
  <div class="clinic-sheet-letterhead">${buildClinicLetterheadBlockHtml(opts.clinic)}</div>
  ${s.bodyHtml}
  ${foot}
</div>`;
    })
    .join('\n');

  return `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escapeHtml(opts.browserTitle)}</title>
<style>${CLINIC_PRINT_BASE_CSS}${MULTI_SHEET_PRINT_EXTRA_CSS}</style></head><body>
${sheetsHtml}
</body></html>`;
}

/** Folha única com timbre — igual filosofia aos Recibos */
export function buildClinicPrintDocumentHtml(opts: ClinicPrintDocumentOptions): string {
  const badge = opts.badgeText
    ? `<div class="doc-badge"><span>${escapeHtml(opts.badgeText)}</span></div>`
    : '';
  const sub = opts.subtitleBelowBadge
    ? `<div class="doc-number">${escapeHtml(opts.subtitleBelowBadge)}</div>`
    : '';
  const foot = opts.footerNote
    ? `<div class="footer-note">${escapeHtml(opts.footerNote)}</div>`
    : '';

  return `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escapeHtml(opts.browserTitle)}</title>
<style>${CLINIC_PRINT_BASE_CSS}</style></head><body>
<div class="sheet">
  <div class="clinic-sheet-letterhead">${buildClinicLetterheadBlockHtml(opts.clinic)}</div>
  ${badge}
  ${sub}
  ${opts.bodyHtml}
  ${foot}
</div>
</body></html>`;
}

/** Assinatura + data alinhadas à esquerda, com área livre para assinar à mão. Único em combos de impressão (evita conflitar com `.signature-line` dentro de `<style>` de anexos). */
export const CLINIC_DOC_SIGNATURE_CSS = `
  .clinic-doc-sign-zone { margin-top: 48px; padding-top: 16px; page-break-inside: avoid; }
  .clinic-doc-sign-zone--compact { margin-top: 36px; padding-top: 12px; }
  .clinic-doc-sign-row { display: flex; flex-wrap: wrap; gap: 28px 44px; align-items: flex-end; justify-content: flex-start; }
  .clinic-doc-sign-slot { flex: 1 1 240px; min-width: 200px; max-width: 400px; text-align: left; }
  .clinic-doc-sign-slot--narrow { flex: 0 1 200px; min-width: 160px; max-width: 220px; }
  .clinic-doc-sign-spacer { min-height: 44px; margin-bottom: 2px; }
  .clinic-doc-sign-spacer--with-img { min-height: 20px; }
  .clinic-doc-sign-rule { border: none; border-top: 1px solid #1f2937; margin: 0 0 8px 0; width: 100%; max-width: 340px; }
  .clinic-doc-sign-caption { font-size: 10px; margin: 2px 0 0 0; color: #333; line-height: 1.45; text-align: left; }
  .clinic-doc-sign-prof-img-wrap { margin-bottom: 4px; }
  .clinic-doc-sign-prof-img-wrap img { max-height: 90px; max-width: 340px; object-fit: contain; vertical-align: bottom; display: inline-block; }
`;

/** Um bloco anexo (exames, indicações, laudo…) dentro da mesma folha */
export function buildEvolutionPrintAttachmentSection(
  categoryLabel: string,
  templateName: string,
  bodyInnerHtml: string,
): string {
  return `<div class="attachment-block">
    <div class="attachment-meta">${escapeHtml(categoryLabel)}</div>
    <h2 class="attachment-title">${escapeHtml(templateName)}</h2>
    <div class="attachment-body">${bodyInnerHtml}</div>
  </div>`;
}

const CLINIC_PRINT_BASE_CSS = `
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; color: #1f2937; margin: 0; padding: 24px; background: #f8fafc; }
  .sheet { max-width: 210mm; margin: 0 auto; background: #fff; padding: 28px 32px 36px; border-radius: 12px; box-shadow: 0 1px 3px rgba(15,23,42,0.08); }
  .clinic-sheet-letterhead {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 3px solid #0d9488;
    padding-bottom: 16px;
    margin-bottom: 20px;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .doc-head-logo { flex-shrink: 0; min-width: 120px; }
  .doc-head-logo img { display: block; max-width: 160px; max-height: 64px; width: auto; height: auto; object-fit: contain; object-position: left top; }
  .doc-head-logo--empty { min-height: 48px; }
  .doc-head-center { flex: 1; min-width: 0; text-align: center; align-self: center; }
  .doc-head-meta { flex-shrink: 0; max-width: 44%; font-size: 10px; color: #6b7280; text-align: right; line-height: 1.5; }
  .clinic-name { font-size: 20px; font-weight: 700; color: #0d9488; margin: 0; }
  .clinic-subtitle { font-size: 12px; color: #6b7280; margin-top: 4px; }
  .doc-badge { text-align: center; margin-bottom: 8px; }
  .doc-badge span { display: inline-block; padding: 6px 18px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; background: #f0fdf4; color: #0d9488; border: 1px solid #a7f3d0; }
  .doc-title { text-align: center; font-size: 17px; font-weight: 700; color: #0f172a; margin: 0 0 4px; }
  .doc-number { text-align: center; font-size: 12px; color: #64748b; margin-bottom: 18px; line-height: 1.45; }
  .section { margin-bottom: 16px; }
  .section-title { font-size: 11px; font-weight: 700; color: #0d9488; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #e5e7eb; }
  .patient-info { background: #f0fdf4; border: 1px solid #a7f3d0; border-radius: 12px; padding: 14px 16px; margin-bottom: 18px; font-size: 13px; line-height: 1.55; color: #334155; }
  .patient-info strong { color: #0f766e; font-weight: 600; }
  .attachment-block { margin-bottom: 24px; page-break-inside: avoid; }
  .attachment-block + .attachment-block { padding-top: 8px; border-top: 1px dashed #e2e8f0; }
  .attachment-meta { font-size: 10px; font-weight: 700; color: #64748b; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 6px; }
  .attachment-title { margin: 0 0 12px; font-size: 17px; font-weight: 700; color: #0d9488; }
  .attachment-body { font-size: 13px; line-height: 1.65; color: #334155; }
  .attachment-body-pre { white-space: pre-wrap; font-family: ui-monospace, 'Cascadia Code', 'Segoe UI Mono', monospace; font-size: 11px; line-height: 1.5; color: #334155; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; }
  .attachment-block--embedded .doc-head,
  .attachment-block--embedded .clinic-sheet-letterhead,
  .attachment-block--embedded .header { display: none !important; }
  .attachment-block--embedded .print-container { padding-top: 0 !important; max-width: none !important; }
  .vitals-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .vital-item { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px; text-align: center; }
  .vital-value { font-size: 16px; font-weight: 700; color: #0d9488; }
  .vital-label { font-size: 11px; color: #64748b; }
  .soap-label { display: inline-block; width: 24px; height: 24px; border-radius: 50%; background: #14b8a6; color: white; text-align: center; line-height: 24px; font-weight: 700; font-size: 13px; margin-right: 8px; }
  .sheet-evolution-footer { margin-top: 36px; text-align: center; border-top: 1px solid #e5e7eb; padding-top: 16px; }
  .sheet-evolution-signature-rule { width: 250px; border-top: 1px solid #334155; margin: 28px auto 4px; }
  .prof-sig-print { text-align: center; margin-bottom: 8px; }
  .prof-sig-print img { max-height: 90px; max-width: 360px; object-fit: contain; display: inline-block; vertical-align: bottom; }
  ${CLINIC_DOC_SIGNATURE_CSS}
  .footer-note { margin-top: 28px; padding-top: 14px; border-top: 1px dashed #e2e8f0; font-size: 10px; color: #94a3b8; text-align: center; line-height: 1.5; }
  .page-break { page-break-after: always; }
  @media print {
    body { background: #fff; padding: 0; }
    .sheet { box-shadow: none; border-radius: 0; max-width: none; }
    @page { size: A4; margin: 14mm; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .clinic-sheet-letterhead { page-break-inside: avoid; break-inside: avoid; }
  }
`;

/** Regras extras só para pacotes com várias folhas timbradas */
const MULTI_SHEET_PRINT_EXTRA_CSS = `
  .sheet.sheet--page-break { margin-top: 28px; }
  @media print {
    .sheet.sheet--page-break {
      margin-top: 0;
      page-break-before: always;
      break-before: page;
    }
  }
`;
